import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiMetaReport, apiMetaReportCsv, eur, errMsg } from "../adminApi";

export default function MetaReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = () => {
    setData(null);
    apiMetaReport(days).then(setData).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [days]); // eslint-disable-line

  const downloadCsv = async () => {
    try {
      const blob = await apiMetaReportCsv(days);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `meta_report_${days}g.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="meta-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Report Agenzia · Meta</h1>
          <p className="mt-1 text-sm text-steel">Ordini per campagna e copertura invii a Meta.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[130px]" data-testid="meta-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" size="sm" onClick={downloadCsv} data-testid="meta-export-csv"><Download className="h-4 w-4" /> Esporta CSV</Button>
        </div>
      </div>

      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-tertiary" /> Ordini per giorno e campagna</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="meta-report-table">
              <TableHeader>
                <TableRow className="border-line hover:bg-transparent">
                  <TableHead>Giorno</TableHead><TableHead>Campagna UTM</TableHead><TableHead>Ordini</TableHead>
                  <TableHead>Fatturato</TableHead><TableHead>Valore medio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data ? (
                  Array.from({ length: 4 }).map((_, i) => <TableRow key={i} className="border-line"><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
                ) : data.rows.length === 0 ? (
                  <TableRow className="border-line"><TableCell colSpan={5} className="py-10 text-center text-sm text-steel">Nessun ordine pagato nel periodo.</TableCell></TableRow>
                ) : data.rows.map((r, i) => (
                  <TableRow key={i} className="border-line">
                    <TableCell className="text-steel">{r.day}</TableCell>
                    <TableCell className="text-chalk">{r.utm_campaign}</TableCell>
                    <TableCell className="text-chalk">{r.orders}</TableCell>
                    <TableCell className="text-chalk">{eur(r.revenue)}</TableCell>
                    <TableCell className="text-steel">{eur(r.aov)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="text-base">Percentuale inviata a Meta (per giorno)</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="meta-coverage-table">
              <TableHeader>
                <TableRow className="border-line hover:bg-transparent">
                  <TableHead>Giorno</TableHead><TableHead>Ordini pagati</TableHead><TableHead>Purchase accettati</TableHead><TableHead>% inviata</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data ? (
                  Array.from({ length: 3 }).map((_, i) => <TableRow key={i} className="border-line"><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
                ) : data.coverage.length === 0 ? (
                  <TableRow className="border-line"><TableCell colSpan={4} className="py-10 text-center text-sm text-steel">Nessun dato.</TableCell></TableRow>
                ) : data.coverage.map((c, i) => (
                  <TableRow key={i} className="border-line">
                    <TableCell className="text-steel">{c.day}</TableCell>
                    <TableCell className="text-chalk">{c.paid_orders}</TableCell>
                    <TableCell className="text-chalk">{c.purchase_accepted}</TableCell>
                    <TableCell className={c.pct_sent >= 90 ? "text-lime" : "text-carrot"}>{c.pct_sent}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
